import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import { FancyShareCreateDto, FancyShareUpdateDto } from '@diritto/common/fancyShare/dtos/fancy-share-edit.dtos';
import { FancySharePublicDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfilePublicRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { HttpStatus } from '@diritto/common/utils/requester/httpStatus';
import { RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import axios from 'axios';
import { FancyShareAction } from 'constants/fancyShare';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useCreateFancyShareMutation } from 'queries/fancyShare/useCreateFancyShareMutation';
import { useDeleteFancyShareMutation } from 'queries/fancyShare/useDeleteFancyShareMutation';
import { useUpdateFancyShareMutation } from 'queries/fancyShare/useUpdateFancyShareMutation';
import { useCallback, useMemo, useState } from 'react';
import { useFileDownload } from 'ui-hooks/useFileDownload';
import { useShare } from 'ui-hooks/useShare';
import { generateImageUrl } from 'utils/generateImageUrl';
import { showToast } from 'utils/modal';
import { SentryService } from 'utils/SentryService';

type BaseFancyShareDispatchParams = {
  action: FancyShareAction.DownloadImage;
  fancyShare: FancySharePublicDto;
};

export type ShareFancyShareDispatchParams = {
  action: FancyShareAction.Share;
  fancyShare: FancySharePublicDto;
  content?: ContentPublicRelayDto;
  authorProfile?: ProfilePublicRelayDto;
};

export type CancelEditFancyShareDispatchParams = {
  action: FancyShareAction.CancelEdit;
};

export type ActiveEditFancyShareDispatchParams = {
  action: FancyShareAction.ActiveEdit;
  fancyShare?: FancySharePublicDto;
};

export type CreateFancyShareDispatchParams = {
  action: FancyShareAction.Create;
  contentId: number;
  episodeId: number;
  data: FancyShareCreateDto;
  onMutate?: OnMutateFn;
};

export type UpdateFancyShareDispatchParams = {
  action: FancyShareAction.Update;
  fancyShare: FancySharePublicDto;
  data: FancyShareUpdateDto;
  onMutate?: OnMutateFn;
};

export type DeleteFancyShareDispatchParams = {
  action: FancyShareAction.Delete;
  fancyShare: FancySharePublicDto;
  onMutate?: OnMutateFn;
};

export type FancyShareDispatchParams =
  | BaseFancyShareDispatchParams
  | CancelEditFancyShareDispatchParams
  | ActiveEditFancyShareDispatchParams
  | CreateFancyShareDispatchParams
  | UpdateFancyShareDispatchParams
  | ShareFancyShareDispatchParams
  | DeleteFancyShareDispatchParams;

export type FancyShareDispatchState = {
  isPending: boolean;
  fancyShare?: FancySharePublicDto;
  isInputFocus: boolean;
  dispatchAction: (params: FancyShareDispatchParams) => void;
};

export const useFancyShareDispatch = (refetch?: (options?: RefetchOptions & RefetchQueryFilters) => Promise<any>) => {
  const { tracker } = useTrackingState();
  const handleShare = useShare();
  const handleDownload = useFileDownload();

  const [dispatchState, setDispatchState] = useState<{
    editFancyShare?: FancySharePublicDto;
    isInputFocus: boolean;
  }>({
    editFancyShare: undefined,
    isInputFocus: false,
  });

  const onSettled = useCallback(() => {
    setDispatchState(prev => ({ ...prev, editReply: undefined, isInputFocus: false }));
    refetch?.();
  }, [refetch]);

  const { mutate: createMutate } = useCreateFancyShareMutation({
    onSettled,
    onSuccess: (res, { contentId, episodeId }) =>
      tracker.track(TrackingEventType.CreateFancyShare, { fancyShareId: res.fancyShare.id, contentId, episodeId }),
    onError: e => {
      if (axios.isAxiosError(e) && e.response?.status === HttpStatus.TOO_MANY_REQUESTS) {
        showToast('하루에 최대 10개까지 이멋공할 수 있어요.');
        return;
      }
      showToast('게시에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const { mutate: updateMutate, isPending: isUpdateMutatePending } = useUpdateFancyShareMutation({
    onSettled,
    onSuccess: (_, { fancyShareId }) => tracker.track(TrackingEventType.UpdateFancyShare, { fancyShareId }),
    onError: () => showToast('수정에 실패했습니다. 다시 시도해주세요.'),
  });

  const { mutate: deleteMutate, isPending: isDeleteMutatePending } = useDeleteFancyShareMutation({
    onSettled,
    onSuccess: (_, { fancyShareId }) => tracker.track(TrackingEventType.DeleteFancyShare, { fancyShareId }),
    onError: () => showToast('삭제에 실패했습니다. 다시 시도해주세요.'),
  });

  const handleDispatchAction = useCallback(
    async (params: FancyShareDispatchParams) => {
      const { action } = params;

      switch (params.action) {
        case FancyShareAction.Share: {
          tracker.track(TrackingEventType.ShareFancyShare, {
            fancyShareId: params.fancyShare.id,
            contentId: params.content?.id,
          });

          const fileUrl = generateImageUrl(params.fancyShare.file);
          const pageUrl = `${window.location.origin}/fancy-shares/${params.fancyShare.id}`;

          const titleAndAuthorName = `${params.content?.title ? `<${params.content.title}> - ` : ''}${
            params.authorProfile?.nickname ?? ''
          }`;
          await handleShare({
            title: titleAndAuthorName,
            text: `${titleAndAuthorName ? `${titleAndAuthorName}\n` : ''}#디리토_이멋공\n${pageUrl}`,
            fileUrl,
            url: pageUrl,
          });
          break;
        }
        case FancyShareAction.DownloadImage: {
          const url = generateImageUrl(params.fancyShare.file);
          try {
            await handleDownload(url);
          } catch (e) {
            SentryService.captureException(e);
            showToast('다운로드에 실패했습니다. 다시 시도해주세요.');
          }
          break;
        }
        case FancyShareAction.Create:
          createMutate(
            { contentId: params.contentId, episodeId: params.episodeId, data: params.data },
            params.onMutate,
          );
          break;
        case FancyShareAction.Update:
          updateMutate({ fancyShareId: params.fancyShare.id, data: params.data }, params.onMutate);
          break;
        case FancyShareAction.Delete:
          deleteMutate({ fancyShareId: params.fancyShare.id }, params.onMutate);
          break;
        case FancyShareAction.ActiveEdit:
          setDispatchState(prev => ({ ...prev, editFancyShare: params.fancyShare, isInputFocus: true }));
          break;
        case FancyShareAction.CancelEdit:
          setDispatchState(prev => ({ ...prev, editFancyShare: undefined, isInputFocus: false }));
          break;
        default:
          console.warn('Not implemented yet', action);
      }
    },
    [createMutate, deleteMutate, handleDownload, handleShare, tracker, updateMutate],
  );

  return useMemo<FancyShareDispatchState>(
    () => ({
      isPending: isUpdateMutatePending || isDeleteMutatePending,
      fancyShare: dispatchState.editFancyShare,
      isInputFocus: dispatchState.isInputFocus,
      dispatchAction: handleDispatchAction,
    }),
    [
      handleDispatchAction,
      isDeleteMutatePending,
      isUpdateMutatePending,
      dispatchState.editFancyShare,
      dispatchState.isInputFocus,
    ],
  );
};
